import { LockOutlined, MobileOutlined } from '@ant-design/icons';
import { App, Button, Card, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getHomePath } from '@/constants/routes';
import { ApiError } from '@/services/api/client';
import * as authApi from '@/services/api/auth';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginResult } from '@/types/auth';
import { enrichAuthUserWithLeadershipTier } from '@/utils/auth-profile';
import { normalizeAuthUser } from '@/utils/auth-user';

interface LoginFormValues {
  phoneNumber: string;
  password: string;
}

export function LoginPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((state) => state.setSession);
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: LoginFormValues) => {
    setSubmitting(true);
    try {
      const result = await authApi.login({
        phoneNumber: values.phoneNumber,
        password: values.password,
      });
      const baseUser = normalizeAuthUser(result.user as LoginResult['user'] & Record<string, unknown>);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: baseUser,
      });

      const user = await enrichAuthUserWithLeadershipTier(baseUser);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user,
      });

      const from = (location.state as { from?: string } | null)?.from;
      const target = from && from !== '/login' ? from : getHomePath(user);
      navigate(target, { replace: true });
      message.success('登录成功');
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '登录失败，请稍后重试';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page mobile-login-page">
      <div className="login-page__main">
        <Card className="login-card" bordered={false}>
          <Typography.Title level={3} style={{ marginTop: 0, textAlign: 'center' }}>
            日报系统
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ textAlign: 'center' }}>
            政务内部日报协作平台（账号密码登录）
          </Typography.Paragraph>
          <Form<LoginFormValues> layout="vertical" size="large" onFinish={onFinish}>
            <Form.Item
              name="phoneNumber"
              label="手机号"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
              ]}
            >
              <Input prefix={<MobileOutlined />} placeholder="请输入手机号" maxLength={11} />
            </Form.Item>
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 8, message: '密码至少 8 位' },
                { max: 64, message: '密码最多 64 位' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入密码"
                maxLength={64}
                autoComplete="current-password"
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={submitting} icon={<LockOutlined />}>
                登录
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
      <footer className="login-page__footer">
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
        >
          晋ICP备2026008032号-1
        </a>
      </footer>
    </div>
  );
}
